import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { FormsModule, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { LegalSetup } from '@app/modules/events/types/legal-setup.type'
import { RepositoriesService } from '@app/services/repositories.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NbButtonModule, NbCardModule, NbCheckboxModule, NbInputModule } from '@nebular/theme'
import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'
import { DEFAULT_PERSONA_FIELD, PersonaField, PersonaFields } from '@personas/interfaces/persona-field.interface'
import { PersonaFactoryService } from '../../models/persona.model'
import { ConditionType, FormElementComponent } from '../form-element/form-element.component'
import { FormLanguagesComponent } from '../form-languages/form-languages.component'
import { FormSocialLoginComponent } from '../form-social-login/form-social-login.component'

/**
 * Represents a list of persona fields
 */
@Component({
  selector: 'ngx-personas-form-elements',
  templateUrl: './form-elements.component.html',
  styleUrls: ['./form-elements.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NbCardModule,
    FormElementComponent,
    MatIconModule,
    NbButtonModule,
    FormLanguagesComponent,
    FormSocialLoginComponent,
    DragDropModule,
    FormsModule,
    NbCheckboxModule,
    NbInputModule,
    ModalComponent
  ]
})
export class FormElementsComponent {
  PersonaFieldTypes = PersonaFieldTypes
  /**
   * Persona fields
   */
  @Input()
  formElements: UntypedFormArray

  /**
   * Persona form group
   */
  @Input()
  form: UntypedFormGroup

  @Input() eventLegalSetup: LegalSetup
  /**
   * Persona has handshake enabled
   */
  @Input()
  hasHandshake = false

  /**
   * Modal ID for the shared condition modal
   */
  readonly conditionModalId = 'condition-modal'

  /**
   * Currently selected element for condition modal
   */
  selectedElement: UntypedFormGroup | null = null

  /**
   * Currently selected condition type for modal
   */
  selectedConditionType: ConditionType = 'displayIf'

  /**
   * Cached country options for performance
   */
  private _cachedCountryOptions: { name: string }[] | null = null

  /**
   * Search term for filtering country values in the modal
   */
  countrySearchTerm: string = ''

  /**
   * Persona field title
   * @param element FormControl
   * @returns string
   */
  title(element) {
    return element.value['label'] ? element.value['label'] : 'New field'
  }

  constructor(private pfs: PersonaFactoryService, private modalService: ModalService, private repositoriesService: RepositoriesService) {}

  /**
   * Updates field location
   * @param event CdkDragDrop
   */
  drop(event: CdkDragDrop<UntypedFormGroup>) {
    if (this.formElements) {
      moveItemInArray(this.formElements.controls, event.previousIndex, event.currentIndex)
      this.resetFieldsWeight()
    }
  }
  /**
   * Adds a new persona field
   */
  addNewElement() {
    this.formElements.push(
      new UntypedFormGroup({
        weight: new UntypedFormControl(this.formElements.controls.length),
        form_element_type_id: new UntypedFormControl(null),
        label: new UntypedFormControl(''),
        name: new UntypedFormControl(''),
        is_required: new UntypedFormControl(0),
        is_unique: new UntypedFormControl(false),
        is_disable_online: new UntypedFormControl(false),
        is_disable_onsite: new UntypedFormControl(false),
        options: this.pfs['generateFieldOptionsFormGroup'](DEFAULT_PERSONA_FIELD),
        form_element_values: new UntypedFormArray(
          DEFAULT_PERSONA_FIELD.form_element_values
            ? DEFAULT_PERSONA_FIELD.form_element_values.map(
                (personaFieldValue, fieldValueIndex) =>
                  new UntypedFormGroup({
                    name: new UntypedFormControl(personaFieldValue.name),
                    slug: new UntypedFormControl(personaFieldValue.slug),
                    weight: new UntypedFormControl(fieldValueIndex),
                    options: this.pfs.generateFieldValueOptionsFormGroup(personaFieldValue)
                  })
              )
            : []
        )
      })
    )
  }

  /**
   * Resets all fields weight based on their current order
   */
  resetFieldsWeight() {
    this.formElements.controls.forEach((item, index) => {
      item.get('weight').setValue(index)
    })
  }
  /**
   * Deletes a persona field by it's index
   * @param index number
   * @param element PersonaFiel
   * @returns void
   */
  deleteField(index: number, element) {
    if (this.disableDelete(element)) {
      return
    }
    // Get the field name before deleting
    const deletedFieldName = element.controls['name'].value

    // Remove the field
    this.formElements.removeAt(index)
    this.resetFieldsWeight()

    // Clean up any displayIf/requiredIf conditions that reference the deleted field
    if (deletedFieldName) {
      this._removeConditionsReferencingField(deletedFieldName)
    }
  }

  /**
   * Removes displayIf and requiredIf conditions that reference a deleted field
   * @param fieldName The name of the deleted field
   */
  private _removeConditionsReferencingField(fieldName: string): void {
    if (!this.formElements?.controls) return

    this.formElements.controls.forEach((control) => {
      const element = control as UntypedFormGroup
      const options = element.controls['options'] as UntypedFormGroup
      if (!options) return

      // Clean up displayIf conditions
      this._cleanupCondition(options, 'displayIf', fieldName)

      // Clean up requiredIf conditions
      this._cleanupCondition(options, 'requiredIf', fieldName)
    })
  }

  /**
   * Cleans up a specific condition type by removing references to a deleted field
   * @param options The options form group
   * @param conditionType 'displayIf' or 'requiredIf'
   * @param fieldName The name of the deleted field
   */
  private _cleanupCondition(options: UntypedFormGroup, conditionType: 'displayIf' | 'requiredIf', fieldName: string): void {
    const conditionControl = options.get(conditionType)
    if (!conditionControl) return

    const conditions = conditionControl.value
    if (!conditions) return

    // Handle array of conditions
    if (Array.isArray(conditions)) {
      const filtered = conditions.filter((c: { field: string }) => c.field !== fieldName)
      if (filtered.length === 0) {
        // No conditions left, set to null
        conditionControl.setValue(null)
      } else if (filtered.length === 1) {
        // Single condition, store as object
        conditionControl.setValue(filtered[0])
      } else {
        // Multiple conditions, store as array
        conditionControl.setValue(filtered)
      }
    } else if (conditions.field === fieldName) {
      // Single condition object that matches the deleted field
      conditionControl.setValue(null)
    }
  }

  /**
   * List of persona fields types in the persona
   * @returns number[]
   */
  elementTypes(): number[] {
    const personaFields = this.formElements.value as PersonaFields
    return personaFields.map((data) => data['form_element_type_id'])
  }

  /**
   * Disables changing a persona field required state
   * @param element FormGroup
   * @returns boolean
   */
  disableRequiredChanges(element: UntypedFormGroup) {
    // Disable required field for handshake forms
    if (element['value']['form_element_type_id'] === PersonaFieldTypes.EMAIL && this.hasHandshake) {
      if (!element.value.is_required) {
        element.patchValue({
          is_required: this.mergeRequiredStates(element.value.is_required, 1),
          is_disable_online: 0
        })
      }
      return true
    }
    // Disable required fields for DET forms
    if (this.eventLegalSetup !== 'NONE' && this.isLegalServiceRequiredField(element)) {
      element.patchValue({
        is_required: 3,
        is_disable_online: 0,
        is_disable_onsite: 0
      })
      return true
    }
    if (
      [PersonaFieldTypes.EMAIL, PersonaFieldTypes.PHONE, PersonaFieldTypes.FULLNAME].includes(element['value']['form_element_type_id']) &&
      this.form.controls['enable_payment'].value
    ) {
      // Enforce online required state for fields that are required for payments
      element.patchValue({
        is_required: this.mergeRequiredStates(element.value.is_required, 1),
        is_disable_online: 0
      })
    }
    return false
  }

  /**
   * Disables changing a persona field required online state
   * @param element FormGroup
   * @returns boolean
   */
  disableRequiredOnlineChanges(element: UntypedFormGroup) {
    return (
      [PersonaFieldTypes.EMAIL, PersonaFieldTypes.PHONE, PersonaFieldTypes.FULLNAME].includes(element['value']['form_element_type_id']) &&
      this.form.controls['enable_payment'].value
    )
  }

  /**
   * Bitwise OR to combine states
   * Merges current and required states without overwriting onsite if already set
   * @param currentState current selected state
   * @param requiredState required online/onsite state
   * @returns
   */
  mergeRequiredStates(currentState: number, requiredState: number): number {
    return currentState | requiredState
  }

  /**
   * Checks if a persona field is required by the legal service (DCT/DTCM)
   * Returns true only if:
   * 1. The event location requires DCT or DTCM (eventLegalSetup !== 'NONE')
   * 2. The field is one of the required types: Fullname, Phone, Email, or Country (with name 'country')
   *
   * When eventLegalSetup is 'NONE', this returns false, allowing fields to be deleted and modified freely.
   * @param element FormGroup
   * @returns boolean - true if field is required by legal service, false otherwise
   */
  public isLegalServiceRequiredField(element): boolean {
    // If location doesn't require DCT/DTCM, field is not required by legal service
    if (this.eventLegalSetup === 'NONE') {
      return false
    }
    // Check if field is one of the required types for DCT/DTCM
    return (
      [PersonaFieldTypes.FULLNAME, PersonaFieldTypes.PHONE, PersonaFieldTypes.EMAIL].includes(element['value']['form_element_type_id']) ||
      (element['value']['form_element_type_id'] === PersonaFieldTypes.COUNTRY && element['value']['name']?.toLowerCase() === 'country')
    )
  }

  /**
   * Disables changing a persona field unique state
   * @param element FormGroup
   * @returns boolean
   */
  disableUniqueChanges(element: UntypedFormGroup) {
    if ((element.value as PersonaField).form_element_type_id === PersonaFieldTypes.EMAIL && this.hasHandshake) {
      if (!element.value.is_unique) {
        element.patchValue({
          is_unique: true
        })
      }
      return true
    }
    return false
  }
  /**
   * Disables deleting persona field if: - The field is FULLNAME, EMAIL, or PHONE and payment is enabled OR The field is EMAIL with a handshake
   * @param element FormGroup
   * @returns boolea
   */
  disableDelete(element: UntypedFormGroup) {
    const isRestrictedField = [PersonaFieldTypes.FULLNAME, PersonaFieldTypes.PHONE, PersonaFieldTypes.EMAIL].includes(element.value.form_element_type_id)

    return (isRestrictedField && this.form.controls['enable_payment'].value) || (element.value.form_element_type_id === PersonaFieldTypes.EMAIL && this.hasHandshake)
  }

  // ============================================
  // Conditional Logic (Display If / Required If)
  // Supports formats:
  // - Single: { field: 'name', value: 'val' } or { field: 'name', value: ['val1', 'val2'] }
  // - Multiple: [{ field: 'name1', value: 'val' }, { field: 'name2', value: ['val1', 'val2'] }]
  // Keys: 'displayIf' and 'requiredIf'
  // ============================================

  /**
   * Gets conditions as an array (normalizes single object to array)
   * @param conditionType 'displayIf' or 'requiredIf'
   */
  private _getConditionsArray(element: UntypedFormGroup, conditionType: 'displayIf' | 'requiredIf' = 'displayIf'): { field: string; value: string | string[] }[] {
    const conditions = element.controls['options'].get(conditionType)?.value
    if (!conditions) return []
    return Array.isArray(conditions) ? conditions : [conditions]
  }

  // ============================================
  // Condition Modal Methods
  // ============================================

  /**
   * Gets the modal header based on selected condition type
   */
  get modalHeader(): string {
    return this.selectedConditionType === 'displayIf' ? 'Display If Conditions' : 'Required If Conditions'
  }

  /**
   * Gets the modal description text based on selected condition type
   */
  get descriptionText(): string {
    return this.selectedConditionType === 'displayIf'
      ? 'Show this field only when the following conditions are met:'
      : 'Make this field required when the following conditions are met:'
  }

  /**
   * Opens the condition modal for a specific element
   */
  openConditionModal(event: { element: UntypedFormGroup; conditionType: ConditionType }): void {
    this.selectedElement = event.element
    this.selectedConditionType = event.conditionType
    this.countrySearchTerm = '' // Clear country search when opening modal
    this.modalService.toggleModal(this.conditionModalId)
  }

  /**
   * Closes the condition modal (called from Done button)
   */
  private _closeConditionModal(): void {
    this.modalService.toggleModal(this.conditionModalId)
    this.selectedElement = null
  }

  /**
   * Handler for modal's onCloseModal event (X button or backdrop click)
   * The modal component already closed itself, so just clean up state
   */
  onModalClosed(): void {
    this.selectedElement = null
  }

  /**
   * Attempts to close the modal only if conditions are valid (for Done button)
   */
  tryCloseConditionModal(): void {
    if (this.hasValidModalConditions()) {
      this._closeConditionModal()
    }
  }

  /**
   * Checks if all selected conditions in the modal have at least one value selected
   */
  hasValidModalConditions(): boolean {
    if (!this.selectedElement) return true
    const conditions = this._getModalConditionsArray()
    if (conditions.length === 0) return true

    for (const condition of conditions) {
      const value = condition.value
      const isEmpty = Array.isArray(value) ? value.length === 0 : !value || (typeof value === 'string' && value.trim() === '')
      if (isEmpty) {
        return false
      }
    }
    return true
  }

  /**
   * Gets conditions array for the modal's selected element
   */
  private _getModalConditionsArray(): { field: string; value: string | string[] }[] {
    if (!this.selectedElement) return []
    const conditions = this.selectedElement.controls['options'].get(this.selectedConditionType)?.value
    if (!conditions) return []
    return Array.isArray(conditions) ? conditions : [conditions]
  }

  /**
   * Saves conditions to the selected element's form control
   */
  private _saveModalConditions(conditions: { field: string; value: string | string[] }[]): void {
    if (!this.selectedElement) return
    const options = this.selectedElement.controls['options'] as UntypedFormGroup
    let valueToSave: any

    if (conditions.length === 0) {
      valueToSave = []
    } else if (conditions.length === 1) {
      valueToSave = conditions[0]
    } else {
      valueToSave = conditions
    }

    if (!options.get(this.selectedConditionType)) {
      options.addControl(this.selectedConditionType, new UntypedFormControl(valueToSave))
    } else {
      options.get(this.selectedConditionType).setValue(valueToSave)
    }
  }

  /**
   * Gets available select fields (RADIO and CHECKBOX types) excluding the selected element
   */
  getSelectFields(): UntypedFormGroup[] {
    if (!this.formElements?.controls) return []

    return this.formElements.controls.filter((el) => {
      const element = el as UntypedFormGroup
      return (
        element !== this.selectedElement &&
        element.controls['form_element_type_id'].value &&
        [PersonaFieldTypes.RADIO, PersonaFieldTypes.AGE_GROUP, PersonaFieldTypes.GENDER, PersonaFieldTypes.COUNTRY].includes(element.controls['form_element_type_id'].value)
      )
    }) as UntypedFormGroup[]
  }

  /**
   * Checks if there are any select fields available
   */
  hasSelectFields(): boolean {
    return this.getSelectFields().length > 0
  }

  /**
   * Checks if a field is selected in the current condition
   */
  isFieldSelected(fieldName: string): boolean {
    const conditions = this._getModalConditionsArray()
    return conditions.some((c) => c.field === fieldName)
  }

  /**
   * Toggles a field in the condition
   */
  toggleConditionField(fieldName: string, selected: boolean): void {
    let conditions = this._getModalConditionsArray()

    if (selected) {
      if (!conditions.some((c) => c.field === fieldName)) {
        conditions = [...conditions, { field: fieldName, value: '' }]
      }
    } else {
      conditions = conditions.filter((c) => c.field !== fieldName)
    }

    this._saveModalConditions(conditions)
  }

  /**
   * Gets the values array for a specific field condition
   */
  private _getFieldValueArray(fieldName: string): string[] {
    const conditions = this._getModalConditionsArray()
    const condition = conditions.find((c) => c.field === fieldName)
    if (!condition) return []
    return Array.isArray(condition.value) ? condition.value : condition.value ? [condition.value] : []
  }

  /**
   * Gets the value string for a specific field condition
   */
  getFieldValue(fieldName: string): string {
    const conditions = this._getModalConditionsArray()
    const condition = conditions.find((c) => c.field === fieldName)
    if (!condition) return ''
    return Array.isArray(condition.value) ? condition.value.join(', ') : condition.value || ''
  }

  /**
   * Checks if a specific value is selected for a field condition
   */
  isConditionValueSelected(fieldName: string, value: string): boolean {
    const values = this._getFieldValueArray(fieldName)
    return values.includes(value)
  }

  /**
   * Toggles a value selection for a field condition
   */
  toggleConditionValueSelection(fieldName: string, value: string, selected: boolean): void {
    const conditions = this._getModalConditionsArray()
    let currentValues = this._getFieldValueArray(fieldName)

    if (selected) {
      if (!currentValues.includes(value)) {
        currentValues = [...currentValues, value]
      }
    } else {
      currentValues = currentValues.filter((v) => v !== value)
    }

    const valueToStore: string | string[] = currentValues.length === 1 ? currentValues[0] : currentValues.length > 1 ? currentValues : ''

    const updatedConditions = conditions.map((c) => (c.field === fieldName ? { ...c, value: valueToStore } : c))
    this._saveModalConditions(updatedConditions)
  }

  /**
   * Gets the label for a form element
   */
  getOtherFieldLabel(element: UntypedFormGroup): string {
    return element.controls['label'].value || element.controls['name'].value || 'Unnamed field'
  }

  /**
   * Gets the values for a form element
   * For Country field type, returns countries from RepositoriesService (cached for performance)
   */
  getOtherFieldValues(element: UntypedFormGroup): any[] {
    const fieldType = element.controls['form_element_type_id'].value

    if (fieldType === PersonaFieldTypes.COUNTRY) {
      // Cache country options to avoid recreating array on every change detection
      if (!this._cachedCountryOptions) {
        this._cachedCountryOptions = this.repositoriesService.CountriesTimezonesRepository.map((c) => ({ name: c.name }))
      }
      return this._cachedCountryOptions
    }

    return element.controls['form_element_values'].value || []
  }

  /**
   * Checks if the selected element is a DTCM required field
   */
  isSelectedElementDTCMRequired(): boolean {
    if (!this.selectedElement) return false
    return this.eventLegalSetup !== 'NONE' && this.isLegalServiceRequiredField(this.selectedElement)
  }

  /**
   * TrackBy function for value options to improve performance
   */
  trackByValueName(index: number, item: { name: string; slug?: string }): string {
    return item.slug || item.name
  }

  /**
   * Gets the slug/identifier to store for a value option
   * For form element values: uses 'slug' (like "otp1")
   * For countries: uses 'name' (country name like "Algeria")
   */
  getValueSlug(valueOption: any): string {
    return valueOption.slug ?? valueOption.name
  }

  /**
   * Gets the display name for a value option
   * For form element values: uses 'name' (display text like "option1")
   * For countries: uses 'name' (country name like "Algeria")
   */
  getValueDisplayName(valueOption: any): string {
    // If slug exists, name is the display text
    // Otherwise (for countries), name is both display and identifier
    return valueOption.name
  }

  /**
   * Gets filtered values based on search term (only applies to Country fields)
   */
  getFilteredValues(element: UntypedFormGroup): any[] {
    const values = this.getOtherFieldValues(element)
    const fieldType = element.controls['form_element_type_id'].value

    // Only apply search filter for Country fields
    if (fieldType !== PersonaFieldTypes.COUNTRY || !this.countrySearchTerm.trim()) {
      return values
    }

    const searchLower = this.countrySearchTerm.toLowerCase()
    return values.filter((v) => v.name.toLowerCase().includes(searchLower))
  }

  /**
   * Checks if a field should show search (has many values like Country)
   */
  shouldShowSearch(element: UntypedFormGroup): boolean {
    return element.controls['form_element_type_id'].value === PersonaFieldTypes.COUNTRY
  }
}
